import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Check,
  Package,
  PackageSearch,
  RefreshCw,
  School,
  Shirt,
  SlidersHorizontal,
  TriangleAlert,
  X,
} from "lucide-react";
import { API_BASE_URL } from "../Config.js";
import View from "./View.js";
import styles from "./FilterPage.module.css";

const getStoreId = () => JSON.parse(sessionStorage.getItem("user"))?.storeId;

// Each checkbox combination maps to its own endpoint; nothing ticked loads all items
const getStockRequest = ({ school, itemType }, storeId) => {
  if (school && itemType) {
    return {
      url: `${API_BASE_URL}/user/filter/item_category/item_type`,
      params: { schoolCode: school, itemType, storeId },
    };
  }
  if (itemType) {
    return {
      url: `${API_BASE_URL}/user/filter/item_list_type`,
      params: { itemType, storeId },
    };
  }
  if (school) {
    return {
      url: `${API_BASE_URL}/user/filter/item_list_school_code`,
      params: { schoolCode: school, storeId },
    };
  }
  return {
    url: `${API_BASE_URL}/user/filter/getAllItems`,
    params: { storeId },
  };
};

const FILTER_TYPES = [
  { key: "useSchool", label: "School", hint: "Show stock for one school", icon: School },
  { key: "useItemType", label: "Item type", hint: "Show stock for one item type", icon: Shirt },
];

const INITIAL_DRAFT = { useSchool: true, useItemType: true, school: "", itemType: "" };

const toFilters = (draft) => ({
  school: draft.useSchool ? draft.school : "",
  itemType: draft.useItemType ? draft.itemType : "",
});

const describeFilters = ({ school, itemType }) => {
  if (school && itemType) return `${itemType} items for ${school}`;
  if (school) return `All items for ${school}`;
  if (itemType) return `${itemType} items across all schools`;
  return "Nothing ticked — every item in the store will be loaded.";
};

const FilterPage = () => {
  const [schoolList, setSchoolList] = useState([]);
  const [allItemTypes, setAllItemTypes] = useState([]);
  const [schoolItemTypes, setSchoolItemTypes] = useState([]);
  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [appliedDraft, setAppliedDraft] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);

  useEffect(() => {
    const storeId = getStoreId();
    if (!storeId) {
      console.error("Store ID not found in session storage");
      return;
    }

    axios
      .get(`${API_BASE_URL}/user/filter/getSchool`, { params: { storeId } })
      .then((response) => setSchoolList(response.data))
      .catch((error) => console.error("Error fetching schools:", error));

    axios
      .get(`${API_BASE_URL}/user/filter/item_type`, { params: { storeId } })
      .then((response) => setAllItemTypes(response.data))
      .catch((error) => console.error("Error fetching item types:", error));
  }, []);

  // With School ticked, only offer the item types that school stocks
  const draftSchool = draft.useSchool ? draft.school : "";

  useEffect(() => {
    const storeId = getStoreId();
    if (!draftSchool || !storeId) {
      setSchoolItemTypes([]);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/user/filter/school/item_type`, {
        params: { schoolCode: draftSchool, storeId },
      })
      .then((response) => {
        if (!cancelled) setSchoolItemTypes(response.data);
      })
      .catch((error) => console.error("Error fetching item types for school:", error));

    return () => {
      cancelled = true;
    };
  }, [draftSchool]);

  // Close the filter modal on Escape
  useEffect(() => {
    if (!showFilterModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShowFilterModal(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showFilterModal]);

  const loadStock = (nextDraft) => {
    const storeId = getStoreId();
    if (!storeId) {
      setError("Store ID not found. Please log in again.");
      return;
    }

    // Only the most recent request may update the page
    const requestId = ++latestRequest.current;
    const { url, params } = getStockRequest(toFilters(nextDraft), storeId);

    setLoading(true);
    setError("");

    axios
      .get(url, { params })
      .then((response) => {
        if (requestId !== latestRequest.current) return;
        setData(Array.isArray(response.data) ? response.data : []);
        setAppliedDraft(nextDraft);
      })
      .catch((error) => {
        if (requestId !== latestRequest.current) return;
        console.error("Error fetching stock:", error);
        setError("Could not load stock. Check your connection and try again.");
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  };

  const toggleFilterType = (key) => {
    setDraft((current) => ({
      ...current,
      [key]: !current[key],
      // The item type list depends on the school, so changing it clears the item type
      ...(key === "useSchool" ? { itemType: "" } : {}),
    }));
  };

  const openFilters = () => {
    if (appliedDraft) setDraft(appliedDraft);
    setShowFilterModal(true);
  };

  const closeFilters = () => setShowFilterModal(false);

  const handleApply = (event) => {
    event.preventDefault();
    setShowFilterModal(false);
    loadStock(draft);
  };

  const refreshData = () => {
    if (appliedDraft) loadStock(appliedDraft);
  };

  const draftFilters = toFilters(draft);
  const missingSchool = draft.useSchool && !draft.school;
  const missingItemType = draft.useItemType && !draft.itemType;
  const canApply = !missingSchool && !missingItemType;
  const itemTypeLocked = draft.useSchool && !draft.school;
  const itemTypeOptions = draft.useSchool ? schoolItemTypes : allItemTypes;

  let missingMessage = "";
  if (missingSchool && missingItemType) missingMessage = "Choose a school and an item type to continue.";
  else if (missingSchool) missingMessage = "Choose a school to continue.";
  else if (missingItemType) missingMessage = "Choose an item type to continue.";

  const appliedFilters = appliedDraft ? toFilters(appliedDraft) : null;
  const activeFilterCount = appliedFilters
    ? [appliedFilters.school, appliedFilters.itemType].filter(Boolean).length
    : 0;
  const viewKey = appliedFilters ? `${appliedFilters.school}|${appliedFilters.itemType}` : "";

  return (
    <>
      {/* Filter modal — rendered outside the page so it covers the whole dashboard */}
      {showFilterModal && (
        <div className={styles.modalOverlay} onClick={closeFilters}>
          <form
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="stock-filter-title"
            onClick={(event) => event.stopPropagation()}
            onSubmit={handleApply}
          >
            <div className={styles.modalHeader}>
              <div>
                <h3 id="stock-filter-title" className={styles.modalTitle}>
                  Filter stock
                </h3>
                <p className={styles.modalSubtitle}>
                  Tick School, Item type or both. Leave both unticked to load every item.
                </p>
              </div>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={closeFilters}
                aria-label="Close filters"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div role="group" aria-labelledby="stock-filter-by">
                <p id="stock-filter-by" className={styles.sectionLabel}>
                  Filter by
                </p>
                <div className={styles.checkGrid}>
                  {FILTER_TYPES.map(({ key, label, hint, icon: Icon }) => {
                    const checked = draft[key];
                    return (
                      <label
                        key={key}
                        className={`${styles.checkCard} ${checked ? styles.checkCardOn : ""}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.checkInput}
                          checked={checked}
                          onChange={() => toggleFilterType(key)}
                        />
                        <span className={styles.checkBox} aria-hidden="true">
                          {checked && <Check size={13} strokeWidth={3} />}
                        </span>
                        <span className={styles.checkText}>
                          <span className={styles.checkTitle}>
                            <Icon size={15} aria-hidden="true" />
                            {label}
                          </span>
                          <span className={styles.checkHint}>{hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {draft.useSchool && (
                <div className={styles.field}>
                  <label htmlFor="stock-school" className={styles.label}>
                    School
                  </label>
                  <div className={styles.selectWrap}>
                    <School size={16} className={styles.selectIcon} aria-hidden="true" />
                    <select
                      id="stock-school"
                      className={styles.select}
                      value={draft.school}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, school: event.target.value, itemType: "" }))
                      }
                    >
                      <option value="">Select a school</option>
                      {schoolList.map((school) => (
                        <option key={school} value={school}>
                          {school}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {draft.useItemType && (
                <div className={styles.field}>
                  <label htmlFor="stock-item-type" className={styles.label}>
                    Item type
                  </label>
                  <div className={styles.selectWrap}>
                    <Shirt size={16} className={styles.selectIcon} aria-hidden="true" />
                    <select
                      id="stock-item-type"
                      className={styles.select}
                      value={draft.itemType}
                      disabled={itemTypeLocked}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, itemType: event.target.value }))
                      }
                    >
                      <option value="">
                        {itemTypeLocked ? "Select a school first" : "Select an item type"}
                      </option>
                      {itemTypeOptions.map((itemType) => (
                        <option key={itemType} value={itemType}>
                          {itemType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div
                className={`${styles.scopeNote} ${canApply ? "" : styles.scopeNoteWarn}`}
                aria-live="polite"
              >
                {canApply ? (
                  <PackageSearch size={16} aria-hidden="true" />
                ) : (
                  <TriangleAlert size={16} aria-hidden="true" />
                )}
                <span>{canApply ? describeFilters(draftFilters) : missingMessage}</span>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={closeFilters}>
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={!canApply || loading}
              >
                {!draftFilters.school && !draftFilters.itemType ? "Load all items" : "Apply filters"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.page}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <span className={styles.headerIcon} aria-hidden="true">
              <Package size={20} />
            </span>
            <h1 className={styles.title}>View Stock</h1>
            <div className={styles.chips}>
              {appliedFilters ? (
                <>
                  {appliedFilters.school && (
                    <span className={styles.chip}>
                      <School size={13} aria-hidden="true" />
                      {appliedFilters.school}
                    </span>
                  )}
                  {appliedFilters.itemType && (
                    <span className={styles.chip}>
                      <Shirt size={13} aria-hidden="true" />
                      {appliedFilters.itemType}
                    </span>
                  )}
                  {activeFilterCount === 0 && <span className={styles.chip}>All items</span>}
                </>
              ) : (
                <span className={styles.chipMuted}>No stock loaded</span>
              )}
            </div>
          </div>

          <div className={styles.headerActions}>
            {appliedFilters && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={refreshData}
                disabled={loading}
              >
                <RefreshCw size={15} className={loading ? styles.spin : undefined} aria-hidden="true" />
                Refresh
              </button>
            )}
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openFilters}>
              <SlidersHorizontal size={15} aria-hidden="true" />
              Filters
              {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
            </button>
          </div>
        </header>

        {error && (
          <div className={styles.errorBanner} role="alert">
            <TriangleAlert size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {data.length > 0 ? (
          <View key={viewKey} data={data} loading={loading} onUpdateSuccess={refreshData} />
        ) : (
          <div className={styles.emptyState}>
            {loading ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                <p className={styles.emptyTitle}>Loading stock…</p>
              </>
            ) : (
              <>
                <span className={styles.emptyIcon} aria-hidden="true">
                  <PackageSearch size={28} />
                </span>
                <p className={styles.emptyTitle}>
                  {appliedFilters ? "No stock found" : "Choose what to view"}
                </p>
                <p className={styles.emptyText}>
                  {appliedFilters
                    ? `Nothing found for: ${describeFilters(appliedFilters)}. Try a different filter.`
                    : "Open Filters and tick School, Item type or both."}
                </p>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.emptyAction}`}
                  onClick={openFilters}
                >
                  <SlidersHorizontal size={15} aria-hidden="true" />
                  {appliedFilters ? "Change filters" : "Open filters"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FilterPage;
